"use client"

import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { getDeliveryQueryTenantId } from "@/hooks/queries/delivery-integrations"
import type { DeliveryScopeType } from "@/lib/deliveries/config"
import {
  getAgentDeliverySettingsAction,
  getTenantDeliveryDefaultsAction,
  type AgentDeliverySettingsView,
  type TenantDeliveryDefaultsView,
} from "@/server-actions/delivery-bindings"

export const tenantDeliveryDefaultsQueryKey = (tenantId: string) =>
  ["delivery-bindings", tenantId, "tenant-defaults"] as const

export const agentDeliverySettingsQueryKey = (
  tenantId: string,
  scopeType: DeliveryScopeType,
  scopeId: string,
) => ["delivery-bindings", tenantId, scopeType, scopeId] as const

export function useTenantDeliveryDefaultsQuery() {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)

  return useQuery<TenantDeliveryDefaultsView>({
    queryKey: tenantDeliveryDefaultsQueryKey(tenantId),
    enabled: isAuthenticated,
    queryFn: async () => {
      const result = await getTenantDeliveryDefaultsAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useAgentDeliverySettingsQuery(
  scopeType: DeliveryScopeType,
  scopeId: string,
) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)

  return useQuery<AgentDeliverySettingsView>({
    queryKey: agentDeliverySettingsQueryKey(tenantId, scopeType, scopeId),
    enabled: isAuthenticated && scopeId.length > 0,
    queryFn: async () => {
      const result = await getAgentDeliverySettingsAction({
        scopeType,
        scopeId,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}
